import {trackAuthEvent} from '@/shared/utils/userManager';

interface SessionUserManager {
    getUser(): Promise<unknown>;
    querySessionStatus(): Promise<unknown>;
    removeUser(): Promise<void>;
    events: {
        addUserLoaded(callback: () => void): void;
        addUserSignedOut(callback: () => void): void;
    };
}

// When the user signs out at the STS (directly, or because SDB Identity passed
// it on via front-channel logout), the oidc-client session monitor raises
// `userSignedOut`. We then remove the stored user and redirect to the STS
// straight away.
//
// That signal can only be trusted in a browser that sends third-party cookies
// to the STS. Safari and Firefox don't, and neither does Chrome incognito.
// There the check session iframe can't read the session cookie, reports
// "changed" immediately, and the `prompt=none` check that follows fails too.
// So `userSignedOut` fires right after every page load while the session is
// still alive. Signing out there would make the STS send the user straight
// back, the monitor would start over, and we'd be stuck in a redirect loop.
//
// That's why we first check once whether `querySessionStatus` works here at
// all, and only act on a later `userSignedOut` if it did. If it didn't, the
// behaviour stays as it was: the user drops out when the access token expires.
//
// We deliberately don't go through USER_SIGNED_OUT. That sets state.oidc.user
// to null, and many components read `state.oidc.user.profile` in stateChanged
// without a null check. The first one to crash aborts redux's listener loop,
// so aca-app never sees the change and no redirect follows. SIGNIN leaves the
// state alone and navigates away from the page.
export function connectStsSignOut(
    userManager: SessionUserManager,
    dispatch: (action: {type: string}) => void,
): void {
    let monitorIsReliable = false;
    let checkInProgress = false;

    const checkMonitor = () => {
        if (monitorIsReliable || checkInProgress) return;
        checkInProgress = true;

        userManager
            .querySessionStatus()
            .then((session) => {
                monitorIsReliable = !!session;
            })
            .catch((error: unknown) => trackAuthEvent('session monitor unreliable', error))
            .finally(() => {
                checkInProgress = false;
            });
    };

    userManager
        .getUser()
        .then((user) => {
            if (user) checkMonitor();
        })
        .catch(() => {});
    userManager.events.addUserLoaded(checkMonitor);

    userManager.events.addUserSignedOut(() => {
        trackAuthEvent('user signed out at sts');

        if (!monitorIsReliable) return;

        userManager
            .removeUser()
            .catch(() => {})
            .then(() => dispatch({type: 'SIGNIN'}));
    });
}
